<template>
  <div class="relative" ref="rootEl">
    <button
      @click="isOpen = !isOpen"
      :title="label"
      :aria-label="label"
      class="relative h-7 w-7 flex items-center justify-center rounded-md border transition-colors"
      :class="isOpen
        ? 'bg-blue-600 border-blue-600 text-white dark:bg-blue-600 dark:border-blue-600 dark:text-white'
        : badgeCount > 0
          ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
          : 'border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark'"
    >
      <component :is="icon" class="h-4 w-4" />
      <span v-if="badgeCount > 0"
        class="absolute -top-1.5 -right-1.5 flex items-center justify-center h-3.5 min-w-[14px] px-0.5 rounded-full text-[9px] font-bold leading-none"
        :class="isOpen ? 'bg-white text-blue-700' : 'bg-blue-600 text-white'">
        {{ badgeCount }}
      </span>
    </button>

    <div v-if="isOpen" class="absolute right-0 top-full mt-1 z-50 max-h-64 overflow-y-auto rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark shadow-lg" :class="panelClass">
      <slot :close="() => { isOpen = false }" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

defineProps({
  icon:       { type: [Object, Function], required: true },
  label:      { type: String, required: true },
  badgeCount: { type: Number, default: 0 },
  panelClass: { type: String, default: 'w-48' },  // override for short lists (e.g. Day/Week/Month) so the panel doesn't run wider than it needs to
})

// v-model:open — controllable from outside (e.g. the "t" shortcut opening the
// Teachers dropdown), defaults to a local ref when unbound.
const isOpen = defineModel('open', { default: false })
const rootEl = ref(null)

// Skip when this copy isn't rendered (display:none): CalendarFilterBar mounts both a
// labeled and an icon-only Teachers dropdown sharing one v-model:open, and the hidden
// one treating every click as "outside" closed the visible one mid-click, before the
// option's own click handler could fire.
const onClickOutside = e => {
  if (rootEl.value && rootEl.value.getClientRects().length && !rootEl.value.contains(e.target)) isOpen.value = false
}
// Esc with text in a FilterOptionList search box is left to that box (it clears the
// query); only an Esc on an empty box closes the dropdown. Checked by attribute, not
// rootEl, since CalendarFilterBar's hidden twin dropdown shares this open state.
const onKeydown = e => {
  if (e.key === 'Escape' && e.target?.hasAttribute?.('data-filter-search') && e.target.value) return
  if (e.key === 'Escape' && isOpen.value) {
    e.stopPropagation()
    isOpen.value = false
  }
}
onMounted(() => {
  document.addEventListener('click', onClickOutside, true)
  document.addEventListener('keydown', onKeydown, true)
})
onUnmounted(() => {
  document.removeEventListener('click', onClickOutside, true)
  document.removeEventListener('keydown', onKeydown, true)
})
</script>
